import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Heart, Briefcase, Coins, Gem, Sun, Moon, Star,
  Calendar, MapPin, Clock, FileText, Shield
} from 'lucide-react';
import { SectionTitle, GoldDivider } from './premium/Decor';

const ITEMS = [
  { icon: Sun, hi: 'ग्रह स्थिति', en: 'Planet Position' },
  { icon: Moon, hi: 'चंद्र राशि', en: 'Moon Sign' },
  { icon: Star, hi: 'नक्षत्र', en: 'Nakshatra' },
  { icon: Calendar, hi: 'दशा विश्लेषण', en: 'Dasha Analysis' },
  { icon: Heart, hi: 'विवाह योग', en: 'Marriage Yoga' },
  { icon: Briefcase, hi: 'करियर मार्ग', en: 'Career Path' },
  { icon: Coins, hi: 'धन योग', en: 'Wealth Yoga' },
  { icon: Gem, hi: 'रत्न सुझाव', en: 'Gem Suggestion' },
  { icon: MapPin, hi: 'शुभ दिशा', en: 'Lucky Direction' },
  { icon: Clock, hi: 'शुभ मुहूर्त', en: 'Auspicious Time' },
  { icon: FileText, hi: '60+ पेज', en: '60+ Pages' },
  { icon: Shield, hi: 'दोष निवारण', en: 'Dosh Remedies' },
];

/**
 * Soft edge-fade wrapper — image bleeds into the cream section background.
 * No hard rectangle borders. Uses radial mask so corners disappear.
 */
const MergedImage = ({ src, alt, className = '', maskShape = 'ellipse' }) => {
  const mask =
    maskShape === 'ellipse'
      ? 'radial-gradient(ellipse at center, #000 60%, rgba(0,0,0,0.7) 82%, transparent 100%)'
      : 'radial-gradient(circle at center, #000 55%, rgba(0,0,0,0.65) 80%, transparent 100%)';
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover ${className}`}
      style={{
        WebkitMaskImage: mask,
        maskImage: mask,
        filter: 'drop-shadow(0 24px 40px rgba(74,9,18,0.28))',
      }}
    />
  );
};

/**
 * A single "showcase row" — image on one side, copy on the other.
 * On mobile stacks: copy first, then image (natural reading flow).
 */
const ShowcaseRow = ({ img, alt, eyebrow, title, body, tags, reverse = false, priority = false, isHi }) => (
  <div className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
    <div className="relative h-72 sm:h-80 md:h-[440px]">
      <img
        src={img}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        className="w-full h-full object-cover"
        style={{
          WebkitMaskImage:
            'radial-gradient(ellipse at center, #000 58%, rgba(0,0,0,0.78) 80%, transparent 100%)',
          maskImage:
            'radial-gradient(ellipse at center, #000 58%, rgba(0,0,0,0.78) 80%, transparent 100%)',
          filter: 'drop-shadow(0 28px 48px rgba(74,9,18,0.32))',
        }}
      />
    </div>
    <div className="text-center md:text-left">
      <p className={`text-brand-gold-dark uppercase tracking-[0.3em] text-[11px] md:text-xs font-black ${isHi ? 'font-hindi' : ''}`}>
        {eyebrow}
      </p>
      <h3 className={`mt-2 md:mt-3 font-playfair font-black text-2xl sm:text-3xl md:text-4xl text-brand-maroon leading-tight ${isHi ? 'font-hindi-serif' : ''}`}>
        {title}
      </h3>
      <GoldDivider className="!my-3 md:!my-4 !justify-center md:!justify-start" />
      <p className={`text-brand-text-dark text-sm md:text-base leading-relaxed max-w-lg mx-auto md:mx-0 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
        {body}
      </p>
      {tags && (
        <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
          {tags.map((t) => (
            <span
              key={t}
              className={`bg-white/70 backdrop-blur border border-brand-gold/40 text-brand-maroon-dark text-[11px] md:text-xs font-bold px-3 py-1.5 rounded-full ${isHi ? 'font-hindi' : 'font-outfit'}`}
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

const ImageGallerySection = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="gallery-section"
      className="relative bg-brand-cream py-14 md:py-24 overflow-hidden"
    >
      {/* Soft blended cream background — subtle mandala watermark only */}
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-30 pointer-events-none" />
      <div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] pointer-events-none opacity-[0.15]"
        style={{
          backgroundImage: "url('/generated/cosmic-mandala.webp')",
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          WebkitMaskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
          maskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'रिपोर्ट की झलक' : 'Report Preview'}
          title={isHi ? 'आपकी कुंडली में क्या-क्या मिलेगा' : "What You'll See In Your Kundli"}
          subtitle={isHi
            ? 'हर पहलू पर विस्तार से विश्लेषण — Astro Avinash द्वारा manually तैयार।'
            : 'Detailed analysis of every aspect — hand-crafted by Astro Avinash.'}
          language={language}
        />

        {/* Alternating showcase — each image blends into the cream via radial mask, no hard borders */}
        <div className="mt-10 md:mt-16 space-y-16 md:space-y-24">
          <ShowcaseRow
            img="/generated/karma-kundali-book.webp"
            alt="Karma Kundali premium book"
            eyebrow={isHi ? 'आपकी premium book' : 'Your Premium Book'}
            title={isHi ? 'कर्म कुंडली' : 'Karma Kundali'}
            body={isHi
              ? '55+ पेज की सुंदर वैदिक रिपोर्ट — Devanagari में बढ़िया layout, ग्रह-दशा-नक्षत्र-दोष-उपाय हर चीज़ पर विस्तृत analysis। PDF आपकी email पर आ जाएगा।'
              : 'A 55+ page beautifully-laid-out Vedic report — planets, dashas, nakshatras, doshas & remedies covered in depth. Delivered as a PDF to your email.'}
            tags={isHi ? ['55+ पेज', 'व्यक्तिगत', 'PDF email', 'हिंदी'] : ['55+ pages', 'Personal', 'PDF email', 'Hindi']}
            priority
            isHi={isHi}
          />

          <ShowcaseRow
            img="/generated/vedic-birth-chart.webp"
            alt="Vedic birth chart"
            eyebrow={isHi ? 'आपकी जन्म कुंडली' : 'Your Birth Chart'}
            title={isHi ? 'जन्म कुंडली + लग्न' : 'Janm Kundali + Lagna'}
            body={isHi
              ? 'North-Indian style hand-crafted birth chart — आपकी सही birth details के हिसाब से लग्न, चंद्र राशि, सारे 9 ग्रहों की exact positions और 12 भावों का पूरा विवरण।'
              : 'Hand-crafted North-Indian style birth chart — Lagna, Moon sign, exact positions of all 9 planets and full breakdown of the 12 houses tailored to your birth details.'}
            tags={isHi ? ['12 भाव', '9 ग्रह', '27 नक्षत्र'] : ['12 Houses', '9 Planets', '27 Nakshatras']}
            reverse
            isHi={isHi}
          />

          <ShowcaseRow
            img="/generated/dasha-timeline.webp"
            alt="Dasha timeline"
            eyebrow={isHi ? 'अगले 20 वर्ष' : 'Next 20 Years'}
            title={isHi ? 'महादशा roadmap' : 'Mahadasha Roadmap'}
            body={isHi
              ? 'शुक्र · गुरु · शनि · बुध · चंद्र — कौन सी दशा कब active है, उसका आप पर क्या effect होगा, और कैसे उसको आप अपने favour में करें। सारी exact timing।'
              : 'Venus · Jupiter · Saturn · Mercury · Moon — which planetary period is active when, its effect on you, and how to turn it in your favour. Exact timings.'}
            tags={isHi ? ['20 वर्ष', 'Timings', 'Antar-dasha'] : ['20 years', 'Timings', 'Antar-dasha']}
            isHi={isHi}
          />

          <ShowcaseRow
            img="/generated/astrologer-desk.webp"
            alt="Personal remedies — mantras, gems, daan"
            eyebrow={isHi ? 'व्यक्तिगत उपाय' : 'Personal Remedies'}
            title={isHi ? 'मंत्र · रत्न · दान' : 'Mantras · Gems · Daan'}
            body={isHi
              ? 'आपकी कुंडली के हिसाब से exact मंत्र (जाप कब और कितना), कौन सा रत्न पहनें, किसका दान करें, और कौन-कौन से दोष निवारण के simple daily उपाय।'
              : 'Exact mantras (when and how many jaap) as per your chart, which gemstone to wear, what to donate, and simple daily practices to neutralise doshas.'}
            tags={isHi ? ['मंत्र', 'रत्न', 'दान', 'दोष उपाय'] : ['Mantras', 'Gems', 'Daan', 'Dosh remedies']}
            reverse
            isHi={isHi}
          />
        </div>

        {/* 12 feature icons — mobile-first 3 col grid */}
        <div className="mt-16 md:mt-24">
          <p className={`text-center text-brand-gold-dark uppercase tracking-[0.3em] text-xs font-black mb-6 ${isHi ? 'font-hindi' : ''}`}>
            {isHi ? 'और भी बहुत कुछ' : 'And much more'}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {ITEMS.map((it, i) => {
              const Icon = it.icon;
              return (
                <div
                  key={i}
                  className="group relative bg-white/70 backdrop-blur rounded-xl md:rounded-2xl border border-brand-gold/25 p-3 md:p-5 text-center hover:border-brand-gold hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="w-10 h-10 md:w-14 md:h-14 mx-auto rounded-xl md:rounded-2xl bg-gradient-to-br from-brand-gold-light to-brand-gold/40 flex items-center justify-center mb-2 md:mb-3 group-hover:from-brand-maroon group-hover:to-brand-maroon-dark transition-all">
                    <Icon className="w-4 h-4 md:w-6 md:h-6 text-brand-maroon group-hover:text-brand-gold-light transition-colors" />
                  </div>
                  <h3 className={`font-semibold text-[10px] md:text-sm text-brand-text-dark leading-tight ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                    {isHi ? it.hi : it.en}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageGallerySection;
